import { useState, useEffect } from 'react';

import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { useAuth } from '@/hooks/useAuth';
import { TrackList } from '@/components/tracks/TrackList';
import { AddTrackModal } from '@/components/tracks/AddTrackModal';
import { usePlaylist } from '@/hooks/usePlaylist';
import { useTracks } from '@/hooks/useTracks';
import { useLiveSession } from '@/hooks/useLiveSession';
import { LiveSessionService } from '@/services/liveSession.service';
import { ChatPanel } from '@/components/chat/ChatPanel';
import { CollaboratorsModal } from '@/components/playlists/CollaboratorsModal';
import { UserService } from '@/services/user.service';
import type { User } from '@/types';
import { useParams, useNavigate } from 'react-router-dom';

export function PlaylistDetailPage() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { user } = useAuth();

    // Real-time hooks
    const { playlist, isLoading: playlistLoading, error: playlistError } = usePlaylist(id);
    const { tracks, isLoading: tracksLoading } = useTracks(id);

    const [activeTab, setActiveTab] = useState<'tracks' | 'chat' | 'collaborators'>('tracks');
    const [showAddTrackModal, setShowAddTrackModal] = useState(false);
    const [showCollaboratorsModal, setShowCollaboratorsModal] = useState(false);
    const [activeSessionId, setActiveSessionId] = useState<string | null>(null);
    const [collaboratorUsers, setCollaboratorUsers] = useState<User[]>([]);

    // Live Session Hook (for creating)
    const { createSession } = useLiveSession();

    // Check for active session
    useEffect(() => {
        if (!id) return;
        LiveSessionService.getSessionByPlaylist(id).then(session => {
            if (session) setActiveSessionId(session.id);
        });
    }, [id]);

    // Fetch collaborators when playlist updates
    useEffect(() => {
        if (!playlist) return;
        const userIds = Object.keys(playlist.collaborators);
        if (userIds.length > 0) {
            UserService.getUsersByIds(userIds).then(setCollaboratorUsers);
        } else {
            setCollaboratorUsers([]);
        }
    }, [playlist]);

    const handleStartSession = async () => {
        if (!playlist || !id) return;
        try {
            await createSession(id, playlist.name, tracks.length);
            navigate(`/playlists/${id}/live`);
        } catch (error) {
            console.error('Failed to start session', error);
        }
    };

    const handleJoinSession = () => {
        if (activeSessionId && id) {
            navigate(`/playlists/${id}/live`);
        }
    };

    // If playlist is not found (and not loading), redirect
    if (!playlistLoading && !playlist && !playlistError) {
        // We might want to show a 404 or redirect
        // navigate('/playlists'); // Side effect in render is bad? better in useEffect.
        // For now, render null is safe-ish, or a "Not Found" message.
    }

    const isLoading = playlistLoading || tracksLoading;

    if (isLoading) return <LoadingSpinner fullScreen />;
    if (playlistError || !playlist) return <div className="p-5 text-center text-danger">Error loading playlist or not found.</div>;

    const isOwner = user?.id === playlist.ownerId;

    const handleTrackAdded = () => {
        // No need to reload, real-time listener handles it.
        // Just close modal if desired, but actually AddTrackModal might keep open for multiple adds.
        // But the modal prop is 'onTrackAdded' which usually implies "done".
        // Let's assume we don't need to do anything but maybe show a toast (not implemented yet).
    };

    return (
        <div className="container-fluid p-0">
            {/* Header Section */}
            <div className="bg-body-tertiary border-bottom p-4">
                <div className="container">
                    <div className="d-flex flex-column flex-md-row align-items-center align-items-md-end gap-4">
                        {/* Thumbnail */}
                        <div
                            className="bg-secondary d-flex align-items-center justify-content-center text-white rounded shadow-sm flex-shrink-0"
                            style={{ width: 180, height: 180 }}
                        >
                            {playlist.thumbnail ? (
                                <img src={playlist.thumbnail} alt={playlist.name} className="w-100 h-100 object-fit-cover rounded" />
                            ) : (
                                <i className="bi bi-music-note-beamed display-1"></i>
                            )}
                        </div>

                        {/* Info */}
                        <div className="flex-grow-1 text-center text-md-start">
                            <span className="badge bg-primary mb-2">{playlist.isPublic ? 'Public' : 'Private'}</span>
                            <h1 className="display-5 fw-bold mb-1">{playlist.name}</h1>
                            <p className="lead text-muted mb-3">{playlist.description}</p>

                            <div className="d-flex flex-wrap gap-3 justify-content-center justify-content-md-start text-muted mb-3">
                                <span><i className="bi bi-people me-1"></i> {Object.keys(playlist.collaborators).length} Collaborators</span>
                                <span><i className="bi bi-collection-play me-1"></i> {playlist.metadata?.totalVideos || 0} Tracks</span>
                                <span>{isOwner ? 'Created by You' : 'Shared Playlist'}</span>
                            </div>

                            <div className="d-flex gap-2 justify-content-center justify-content-md-start">
                                <button
                                    className="btn btn-primary btn-lg rounded-pill px-4"
                                    onClick={() => {
                                        if (activeSessionId) handleJoinSession();
                                        else if (isOwner) handleStartSession();
                                        else alert('No active live session. Ask the owner to go live!');
                                    }}
                                    disabled={!isOwner && !activeSessionId}
                                    title={!isOwner && !activeSessionId ? 'Wait for the owner to go live' : 'Start or Join Session'}
                                >
                                    <i className="bi bi-play-fill me-1"></i> {activeSessionId ? 'Join Live' : isOwner ? 'Go Live' : 'Play'}
                                </button>
                                <button
                                    className="btn btn-outline-secondary btn-lg rounded-pill px-4"
                                    onClick={() => setShowCollaboratorsModal(true)}
                                >
                                    <i className="bi bi-person-plus me-1"></i> Invite
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Tabs & Content */}
            <div className="container py-4">
                <ul className="nav nav-tabs mb-4">
                    <li className="nav-item">
                        <button
                            className={`nav-link ${activeTab === 'tracks' ? 'active' : ''}`}
                            onClick={() => setActiveTab('tracks')}
                        >
                            Tracks
                        </button>
                    </li>
                    <li className="nav-item">
                        <button
                            className={`nav-link ${activeTab === 'chat' ? 'active' : ''}`}
                            onClick={() => setActiveTab('chat')}
                        >
                            Chat
                        </button>
                    </li>
                    <li className="nav-item">
                        <button
                            className={`nav-link ${activeTab === 'collaborators' ? 'active' : ''}`}
                            onClick={() => setActiveTab('collaborators')}
                        >
                            Collaborators
                        </button>
                    </li>
                </ul>

                <div className="bg-body rounded shadow-sm p-4" style={{ minHeight: 300 }}>
                    {activeTab === 'tracks' && (
                        <div>
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <h3 className="h5 mb-0">Tracks ({tracks.length})</h3>
                                <button className="btn btn-primary btn-sm" onClick={() => setShowAddTrackModal(true)}>
                                    <i className="bi bi-plus-lg me-1"></i> Add Track
                                </button>
                            </div>

                            {tracks.length === 0 ? (
                                <div className="text-center text-muted py-5">
                                    <div className="mb-3"><i className="bi bi-music-note-list display-4 opacity-25"></i></div>
                                    <h5>No tracks yet</h5>
                                    <button className="btn btn-primary mt-3" onClick={() => setShowAddTrackModal(true)}>
                                        Add Your First Track
                                    </button>
                                </div>
                            ) : (
                                <TrackList
                                    playlistId={id!}
                                    tracks={tracks}
                                    onTrackAction={handleTrackAdded}
                                    isOwner={isOwner}
                                />
                            )}
                        </div>
                    )}
                    {activeTab === 'chat' && (
                        <div style={{ height: '500px' }}>
                            <ChatPanel playlistId={id!} />
                        </div>
                    )}
                    {activeTab === 'collaborators' && (
                        <div>
                            <div className="d-flex justify-content-between align-items-center mb-4">
                                <h3 className="h5 mb-0">Collaborators ({collaboratorUsers.length})</h3>
                                <button className="btn btn-outline-primary btn-sm" onClick={() => setShowCollaboratorsModal(true)}>
                                    <i className="bi bi-person-plus me-1"></i> Invite New
                                </button>
                            </div>
                            <div className="row g-3">
                                {collaboratorUsers.map(u => (
                                    <div key={u.id} className="col-md-6 col-lg-4">
                                        <div className="d-flex align-items-center p-3 border rounded">
                                            <div className="bg-secondary text-white rounded-circle d-flex justify-content-center align-items-center me-3" style={{ width: 48, height: 48 }}>
                                                {u.name?.charAt(0) || u.email.charAt(0)}
                                            </div>
                                            <div>
                                                <div className="fw-bold">{u.name || 'User'}</div>
                                                <div className="text-muted small">{u.email}</div>
                                                {playlist?.ownerId === u.id && <span className="badge bg-primary mt-1">Owner</span>}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            <AddTrackModal
                show={showAddTrackModal}
                onHide={() => setShowAddTrackModal(false)}
                playlistId={id!}
                onTrackAdded={handleTrackAdded}
            />

            {playlist && (
                <CollaboratorsModal
                    show={showCollaboratorsModal}
                    onHide={() => setShowCollaboratorsModal(false)}
                    playlist={playlist}
                    currentCollaboratorUsers={collaboratorUsers}
                />
            )}
        </div>
    );
}
